/**
 * LoginScreen.tsx
 * Teacher email/password sign-in & sign-up. Shown in the task pane until a
 * teacher is authenticated, so their classes and data persist across sessions.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Button from '@components/shared/Button';
import { signInTeacher, signUpTeacher, signInTeacherWithGoogleIdToken } from '@db/firebase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Office: any;

function friendlyError(code: string): string {
  switch (code) {
    case 'auth/invalid-email': return 'That email address looks invalid.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found': return 'Incorrect email or password.';
    case 'auth/email-already-in-use': return 'An account already exists for this email. Try signing in.';
    case 'auth/weak-password': return 'Password should be at least 6 characters.';
    case 'auth/operation-not-allowed': return 'Email/password sign-in is not enabled in Firebase yet.';
    case 'auth/network-request-failed': return 'Network error. Check your connection.';
    default: return 'Something went wrong. Please try again.';
  }
}

export default function LoginScreen(): React.ReactElement {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError('');
    if (!email.trim() || !password) { setError('Enter your email and password.'); return; }
    setBusy(true);
    try {
      if (mode === 'signin') await signInTeacher(email.trim(), password);
      else await signUpTeacher(email.trim(), password);
      // onTeacherAuthChanged in the app will swap to the task pane.
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      setError(friendlyError(code));
    } finally {
      setBusy(false);
    }
  };

  // Google sign-in via the Office auth dialog (popup OAuth is blocked in the
  // task pane WebView, so a separate Office-hosted window does the handshake).
  const signInWithGoogle = () => {
    setError('');
    if (typeof Office === 'undefined' || !Office.context?.ui) {
      setError('Google sign-in is only available inside PowerPoint.');
      return;
    }
    const url = `${window.location.origin}/auth-dialog.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { height: 60, width: 30, promptBeforeOpen: false },
      (res: { status: string; value?: { addEventHandler: (t: unknown, h: (a: { message: string }) => void) => void; close: () => void }; error?: { message?: string } }) => {
        if (res.status !== 'succeeded' || !res.value) {
          setError('Could not open Google sign-in.');
          return;
        }
        const dialog = res.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg: { message: string }) => {
          dialog.close();
          setBusy(true);
          try {
            const msg = JSON.parse(arg.message) as { ok: boolean; googleIdToken?: string; error?: string };
            if (!msg.ok || !msg.googleIdToken) {
              setError(msg.error ?? 'Google sign-in was cancelled.');
              return;
            }
            await signInTeacherWithGoogleIdToken(msg.googleIdToken);
            // onTeacherAuthChanged swaps to the task pane.
          } catch {
            setError('Google sign-in failed. Please try again.');
          } finally {
            setBusy(false);
          }
        });
      },
    );
  };

  return (
    <div className="flex flex-col h-full bg-surface items-center justify-center p-6">
      <motion.div
        className="w-full max-w-sm space-y-6"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="text-center space-y-2">
          <img
            src="/assets/icons/guro_ai.gif"
            alt="GuroHub"
            className="w-16 h-16 rounded-full object-cover border-2 border-brand-500 mx-auto"
          />
          <h1 className="text-lg font-bold text-slate-900">GuroHub</h1>
          <p className="text-xs text-slate-500">
            {mode === 'signin' ? 'Sign in to access your classes' : 'Create a teacher account'}
          </p>
        </div>

        <div className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="username"
            className="w-full bg-surface-elevated border border-surface-border rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-500"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder="Password"
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            className="w-full bg-surface-elevated border border-surface-border rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-500"
          />

          {error && (
            <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{error}</p>
          )}

          <Button fullWidth onClick={submit} loading={busy} disabled={!email.trim() || !password}>
            {mode === 'signin' ? 'Sign In' : 'Create Account'}
          </Button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-surface-border" />
            <span className="text-[10px] text-slate-400 uppercase tracking-wide">or</span>
            <div className="flex-1 h-px bg-surface-border" />
          </div>

          <button
            type="button"
            onClick={signInWithGoogle}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2.5 bg-white border border-surface-border rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-surface-elevated transition-colors disabled:opacity-60"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4" />
            Continue with Google
          </button>
        </div>

        <p className="text-center text-xs text-slate-500">
          {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
          <button
            onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); }}
            className="text-brand-400 font-semibold hover:text-brand-300"
          >
            {mode === 'signin' ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </motion.div>
    </div>
  );
}
